/* eslint-disable indent */
import { AxiosRequestConfig } from 'axios';
import {
  Application,
  RequiredDocument,
  RequiredDocuments,
} from '../../../../../types/api';
import {
  INSTALLER_DASHBOARD_APPLICATIONS_SUMMARY_URL,
  INSTALLER_DASHBOARD_APPLICATIONS_URL,
  USER_APPLICATIONS_URL,
} from '../../constants';
import {
  BadRequest,
  GeneralError,
  ServerError,
  UserNotFound,
} from '../../utils/errors';
import noticeError from '../../utils/errors/noticeError';
import { cleanPDF } from '../utils';
import { api } from './API';
import {
  CompleteDraftApplicationResponse,
  CreateDraftApplicationResponse,
} from './responses/applicationApiResponses';
import { AssetPayload } from '../../modules/loan/asset';

class ApplicationAPI {
  async getUserApplications() {
    try {
      const response = await api().get<Application[]>(USER_APPLICATIONS_URL);
      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async getInstallerDashboardApplications(
    accountId: string,
    accountType: string,
    role: string,
    contactId: string
  ) {
    const response = await api().get<any>(
      INSTALLER_DASHBOARD_APPLICATIONS_URL,
      {
        params: { accountId, accountType, role, contactId },
      }
    );
    return response.data;
  }

  async getInstallerDashboardApplicationsSummary(
    accountId: string,
    accountType: string,
    role: string,
    contactId: string
  ) {
    const response = await api().get<any>(
      INSTALLER_DASHBOARD_APPLICATIONS_SUMMARY_URL,
      {
        params: { accountId, accountType, role, contactId },
      }
    );
    return response.data;
  }

  async getApplication(loanId) {
    try {
      const response = await api().get<Application>(
        `${USER_APPLICATIONS_URL}${loanId}/`
      );

      if (
        typeof response.data.loan_version?.maturity_payment_amount === 'string'
      ) {
        const converted = Number(
          response.data.loan_version.maturity_payment_amount
        );
        if (isNaN(converted)) {
          response.data.loan_version.maturity_payment_amount = 0;
        } else {
          response.data.loan_version.maturity_payment_amount = converted;
        }
      }

      if (
        typeof response.data.loan_version?.borrower_advance_rate === 'string'
      ) {
        const converted = Number(
          response.data.loan_version.borrower_advance_rate
        );
        if (isNaN(converted)) {
          response.data.loan_version.borrower_advance_rate = 0;
        } else {
          response.data.loan_version.borrower_advance_rate = converted;
        }
      }

      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async updateApplication(
    loanId: string,
    data: Partial<Application | RequiredDocuments>
  ) {
    try {
      const response = await api().put(
        `${USER_APPLICATIONS_URL}${loanId}/`,
        data
      );
      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async notifyRailway(applicationId: string, formData: FormData) {
    try {
      const response = await api().post<{ formData: any }>(
        `${USER_APPLICATIONS_URL}${applicationId}/notify-railway`,
        formData
      );
      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }
  /**
   * Get the list of principals for a loan
   * @param {string} loanId
   * @returns {Promise<object[]>}
   */
  async getApplicationPrincipals(loanId) {
    try {
      const response = await api().get(
        `${USER_APPLICATIONS_URL}${loanId}/principals`
      );
      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  // Get a required document by id (Railway, postgres)
  async getRequiredDocument(
    requiredDocumentId: string,
    includeNotes = false,
    includeFiles = false
  ) {
    try {
      const response = await api().get<{ data: RequiredDocument }>(
        `${USER_APPLICATIONS_URL}required-document/${requiredDocumentId}?include_notes=${includeNotes}&include_files=${includeFiles}`
      );
      return response.data.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  // Upload a file for a required document (through Railway endpoint)
  async uploadFileForRequiredDocument(data: FormData) {
    try {
      const response = await api().post<{ data: any }>(
        `${USER_APPLICATIONS_URL}required-document/file`,
        data,
        { timeout: 10 * 60 * 1000 }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;
        // Preserve the actual error message from the backend
        if (status === 400 && data?.error) {
          throw new Error(data.error);
        }
        throw getErrorByStatusCode(status, data);
      } else {
        throw getErrorByStatusCode(503, null);
      }
    }
  }

  async uploadDocument(
    applicationId: string,
    data: FormData,
    params: Record<string, string> = {}
  ) {
    const config: AxiosRequestConfig = {
      timeout: 10 * 60 * 1000,
      params,
    };
    const response = await api().post<{ data: RequiredDocument }>(
      `${USER_APPLICATIONS_URL}${applicationId}/documents/`,
      data,
      config
    );
    return response.data.data;
  }

  async uploadApplicationDocumentWithCleaning(
    applicationId: string,
    data: FormData,
    params: Record<string, string> = {}
  ) {
    try {
      return await this.uploadDocument(applicationId, data, params);
    } catch (error) {
      console.error('error', error);
      if (error.response.status >= 400) {
        try {
          const cleaned = await cleanPDF(data, 'file');
          return await this.uploadDocument(applicationId, cleaned, params);
        } catch (cleanError) {
          console.error('cleanError', cleanError);
        }
      }
      noticeError(error);
      if (error.response) {
        const { status, data } = error.response;
        // Preserve the actual error message from the backend
        if (status === 400 && data?.error) {
          throw new Error(data.error);
        }
        throw getErrorByStatusCode(status, data);
      } else {
        throw getErrorByStatusCode(503, null);
      }
    }
  }

  // Upload a file for a SunstoneDocument (salesforce) and RequiredDocument (postgres)
  async uploadApplicationDocument(
    applicationId: string,
    data: FormData,
    params: Record<string, string> = {}
  ) {
    return await this.uploadApplicationDocumentWithCleaning(
      applicationId,
      data,
      params
    );
  }

  async getApplicationDocuments(loanId: string) {
    try {
      const response = await api().get<{
        data: RequiredDocuments;
      }>(`${USER_APPLICATIONS_URL}${loanId}/documents/`);
      return response.data.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async deleteApplicationDocument(documentId: string) {
    try {
      await api().delete<Record<string, never>>(
        `${USER_APPLICATIONS_URL}required-document/${documentId}`
      );
      return;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async downloadFile(fileId: string) {
    try {
      const response = await api().get(
        `${USER_APPLICATIONS_URL}documents/files/${fileId}`,
        {
          responseType: 'blob',
        }
      );

      // Extract the filename from the Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      const fileName = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : 'downloaded-file';

      // Create a Blob from the response data
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });

      // Create a URL for the Blob and trigger the download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async listPrincipals(loanId) {
    try {
      const response = await api().get(
        `${USER_APPLICATIONS_URL}${loanId}/principals`
      );
      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async createPrincipal(loanId, data) {
    try {
      const response = await api().post(
        `${USER_APPLICATIONS_URL}${loanId}/principals`,
        data
      );
      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async updatePrincipal(loanId, principalId, updates) {
    try {
      const response = await api().put(
        `${USER_APPLICATIONS_URL}${loanId}/principals/${principalId}`,
        updates
      );
      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async deletePrincipal(loanId, principalId) {
    try {
      const response = await api().delete(
        `${USER_APPLICATIONS_URL}${loanId}/principals/${principalId}`
      );
      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async getApplicationTasks(loanId) {
    try {
      const response = await api().get(
        `${USER_APPLICATIONS_URL}${loanId}/tasks`
      );
      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async getRulesets() {
    try {
      const response = await api().get(`${USER_APPLICATIONS_URL}rulesets`);
      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async createDraftApplication(): Promise<CreateDraftApplicationResponse> {
    try {
      const response = await api().post<CreateDraftApplicationResponse>(
        `${USER_APPLICATIONS_URL}application-draft/`
      );
      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async updateDraftApplication(
    id: string,
    data
  ): Promise<CreateDraftApplicationResponse> {
    try {
      const response = await api().put(
        `${USER_APPLICATIONS_URL}application-draft/${id}/`,
        data
      );
      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async completeDraftApplication(
    id: string,
    body?: { assets?: ReadonlyArray<AssetPayload> }
  ): Promise<CompleteDraftApplicationResponse> {
    try {
      const response = await api().post<CompleteDraftApplicationResponse>(
        `${USER_APPLICATIONS_URL}application-draft/${id}/complete/`,
        body
      );
      return response.data;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }

  async uploadDocumentDraft(
    draftId: string,
    data: FormData,
    params: Record<string, string> = {}
  ): Promise<any> {
    const config: AxiosRequestConfig = {
      timeout: 10 * 60 * 1000,
      params,
    };
    const response = await api().post<{ data: RequiredDocument }>(
      `${USER_APPLICATIONS_URL}application-draft/${draftId}/files/`,
      data,
      config
    );
    return response.data;
  }

  async deleteDocumentDraft(draftId: string, documentId: string) {
    try {
      await api().delete<Record<string, never>>(
        `${USER_APPLICATIONS_URL}application-draft/${draftId}/files/${documentId}/`
      );
      return;
    } catch (error) {
      noticeError(error);
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
  }
}

export type PatchBorrowerApplicationBody = {
  borrower_project_nickname?: string | null;
  installer_changes_need_review?: false;
};

export type PatchBorrowerApplicationResponse = {
  id: string;
} & PatchBorrowerApplicationBody;

export const patchBorrowerApplication = async (
  applicationId: string,
  body: PatchBorrowerApplicationBody
): Promise<PatchBorrowerApplicationResponse> => {
  try {
    const res = await api().patch<PatchBorrowerApplicationResponse>(
      `${USER_APPLICATIONS_URL}${applicationId}/`,
      body
    );
    return res.data;
  } catch (error) {
    noticeError(error);
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data);
    }
    throw getErrorByStatusCode(503, null);
  }
};

export const getErrorByStatusCode = (statusCode, data = null) => {
  switch (statusCode) {
    case 200:
    case 201:
    case 204:
      break;

    case 400:
      throw new BadRequest({ data } as any);

    case 401:
      throw new UserNotFound({ data } as any);

    case 503:
      throw new ServerError({ data } as any);

    default:
      throw new GeneralError({ data } as any);
  }
};

export const applicationAPI = new ApplicationAPI();
